import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiPlus, FiMinus } = FiIcons;

const FAQ = () => {
  const [openItems, setOpenItems] = useState(new Set([0])); // First item open by default

  const toggleItem = (index) => {
    const newOpenItems = new Set(openItems);
    if (newOpenItems.has(index)) {
      newOpenItems.delete(index);
    } else {
      newOpenItems.add(index);
    }
    setOpenItems(newOpenItems);
  };

  const faqItems = [
    {
      question: "Is this a replacement for my current recovery program?",
      answer: "Absolutely not. We are a powerful complement. We provide the neurological tools for emotional regulation and impulse control that make working the steps more effective. We work alongside your existing support system."
    },
    {
      question: "I'm on medication (e.g., Suboxone, Naltrexone). Is this compatible?",
      answer: "Yes. Medication manages the biological cravings. We build the cognitive skills to manage the psychological, emotional, and environmental triggers. They work beautifully together."
    },
    {
      question: "How is this different from talking to my addiction counselor?",
      answer: "Traditional counseling often focuses on the why of past behaviors. Our EF therapy focuses on the how: How to build the specific brain skills needed to make different choices in the future. It's proactive and skills-based."
    },
    {
      question: "I've relapsed before. How is this different?",
      answer: "We train the specific brain functions that control impulses and decision-making. Most relapses happen when executive functions are overwhelmed. We make those skills stronger than your cravings."
    },
    {
      question: "Will this work if I'm not ready to be completely sober?",
      answer: "Executive function skills help with any level of use reduction you're working toward. Stronger brain skills mean better choices, whether you're aiming for complete abstinence or harm reduction."
    },
    {
      question: "Can I do this while I'm in treatment or rehab?",
      answer: "Yes! EF therapy complements all forms of addiction treatment and actually makes them more effective. Many clients work with us during or after formal treatment programs."
    },
    {
      question: "Why would a speech therapist work on addiction recovery?",
      answer: "Speech-language pathologists are experts in cognitive-communication disorders and executive function. We understand how the brain processes information, controls behavior, and can be retrained—exactly what addiction recovery requires."
    },
    {
      question: "What if I don't want to talk about my addiction history?",
      answer: "We focus on building brain skills, not rehashing your past. While understanding your situation helps us personalize treatment, the work itself is about strengthening cognitive abilities for your future."
    },
    {
      question: "Is this covered by insurance?",
      answer: "Many insurance plans cover speech-language pathology services for cognitive disorders when there's a diagnosis. We help you verify benefits and navigate coverage options."
    },
    {
      question: "How long does it take to see results?",
      answer: "Most clients notice improvements in impulse control and decision-making within 4-6 weeks. Significant changes in recovery stability typically occur within 3-6 months of consistent work."
    }
  ];

  return (
    <section id="faq" className="py-20 bg-gray-50">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl lg:text-5xl font-playfair font-bold text-gray-900 mb-6">
            Frequently Asked{' '}
            <span className="bg-gradient-to-r from-accent-magenta to-accent-purple bg-clip-text text-transparent">
              Questions
            </span>
          </h2>
        </motion.div>

        <div className="space-y-4">
          {faqItems.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl shadow-sm overflow-hidden"
            >
              <button
                onClick={() => toggleItem(index)}
                className="w-full px-6 py-6 text-left flex items-center justify-between hover:bg-gray-50 transition-colors duration-200"
                aria-expanded={openItems.has(index)}
              >
                <h3 className="text-lg font-semibold text-gray-900 pr-4">
                  {item.question}
                </h3>
                <div className="flex-shrink-0">
                  <SafeIcon 
                    icon={openItems.has(index) ? FiMinus : FiPlus} 
                    className="w-5 h-5 text-accent-magenta" 
                  />
                </div>
              </button>
              
              <AnimatePresence>
                {openItems.has(index) && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="px-6 pb-6">
                      <p className="text-gray-600 leading-relaxed">
                        {item.answer}
                      </p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQ;